/**
 * PDFRenderer
 * 
 * Base wrapper around jsPDF providing a clean API for PDF operations.
 * Handles common operations like text, positioning, fonts, and colors.
 */

import { jsPDF } from 'jspdf'
import { CONTROL_CARD_CONFIG } from '../../config'

/**
 * Text styling options
 */
export interface TextStyle {
  size?: number
  font?: string
  weight?: 'normal' | 'bold' | 'italic'
  color?: [number, number, number]
  align?: 'left' | 'center' | 'right'
}

/**
 * Position coordinates
 */
export interface Position {
  x: number
  y: number
}

/**
 * Rectangle dimensions
 */
export interface Rectangle {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Base PDF renderer with common operations
 */
export class PDFRenderer {
  protected doc: jsPDF
  protected readonly config: typeof CONTROL_CARD_CONFIG

  constructor(config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG) {
    this.config = config
    this.doc = new jsPDF()
  }

  /**
   * Get the underlying jsPDF instance
   */
  getDocument(): jsPDF {
    return this.doc
  }

  /**
   * Create a new page
   */
  addPage(): void {
    this.doc.addPage()
  }

  /**
   * Set current page
   */
  setPage(pageNumber: number): void {
    if (typeof (this.doc as any).setPage === 'function') {
      ;(this.doc as any).setPage(pageNumber)
    }
  }

  /**
   * Get total number of pages
   */
  getPageCount(): number {
    return typeof (this.doc as any).getNumberOfPages === 'function'
      ? (this.doc as any).getNumberOfPages()
      : 1
  }

  /**
   * Get page dimensions
   */
  getPageDimensions(): { width: number; height: number } {
    const pageSize = (this.doc as any).internal.pageSize
    return {
      width: pageSize.width ? pageSize.width : pageSize.getWidth(),
      height: pageSize.height ? pageSize.height : pageSize.getHeight(),
    }
  }

  /**
   * Render text with styling
   */
  renderText(text: string, position: Position, style?: TextStyle): void {
    // Apply font
    if (style?.font || style?.weight) {
      this.doc.setFont(
        style?.font || 'helvetica',
        style?.weight || 'normal'
      )
    }

    // Apply font size
    if (style?.size) {
      this.doc.setFontSize(style.size)
    }

    // Apply color
    if (style?.color) {
      this.doc.setTextColor(...style.color)
    }

    // Render text
    if (style?.align) {
      this.doc.text(text, position.x, position.y, { align: style.align })
    } else {
      this.doc.text(text, position.x, position.y)
    }
  }

  /**
   * Render multiple lines of text
   */
  renderMultilineText(
    lines: string[],
    startPosition: Position,
    lineHeight: number,
    style?: TextStyle
  ): number {
    let currentY = startPosition.y

    for (const line of lines) {
      this.renderText(line, { x: startPosition.x, y: currentY }, style)
      currentY += lineHeight
    }

    return currentY
  }

  /**
   * Draw a line
   */
  drawLine(from: Position, to: Position, width: number = 0.05, color?: [number, number, number]): void {
    this.doc.setLineWidth(width)
    
    if (color) {
      this.doc.setDrawColor(...color)
    }
    
    this.doc.line(from.x, from.y, to.x, to.y)
  }

  /**
   * Draw a rectangle
   */
  drawRectangle(
    rect: Rectangle,
    options?: {
      fill?: boolean
      fillColor?: [number, number, number]
      borderColor?: [number, number, number]
      borderWidth?: number
    }
  ): void {
    if (options?.borderWidth) {
      this.doc.setLineWidth(options.borderWidth)
    }

    if (options?.borderColor) {
      this.doc.setDrawColor(...options.borderColor)
    }

    if (options?.fill && options?.fillColor) {
      this.doc.setFillColor(...options.fillColor)
      this.doc.rect(rect.x, rect.y, rect.width, rect.height, 'FD') // Fill and Draw
    } else if (options?.fill) {
      this.doc.rect(rect.x, rect.y, rect.width, rect.height, 'F') // Fill only
    } else {
      this.doc.rect(rect.x, rect.y, rect.width, rect.height, 'S') // Stroke only
    }
  }

  /**
   * Add image to PDF
   */
  addImage(
    imageData: string,
    position: Position,
    dimensions: { width: number; height: number },
    format: 'PNG' | 'JPEG' = 'PNG'
  ): void {
    this.doc.addImage(imageData, format, position.x, position.y, dimensions.width, dimensions.height)
  }

  /**
   * Set font style
   */
  setFont(font: string, weight: 'normal' | 'bold' | 'italic' = 'normal'): void {
    this.doc.setFont(font, weight)
  }

  /**
   * Set font size
   */
  setFontSize(size: number): void {
    this.doc.setFontSize(size)
  }

  /**
   * Set text color
   */
  setTextColor(color: [number, number, number]): void {
    this.doc.setTextColor(...color)
  }

  /**
   * Set draw color for lines and borders
   */
  setDrawColor(color: [number, number, number]): void {
    this.doc.setDrawColor(...color)
  }

  /**
   * Set line width
   */
  setLineWidth(width: number): void {
    this.doc.setLineWidth(width)
  }

  /**
   * Get current font info
   */
  getCurrentFont(): { fontName: string; fontStyle: string } {
    const font = this.doc.getFont()
    return {
      fontName: font.fontName,
      fontStyle: font.fontStyle,
    }
  }

  /**
   * Get current font size
   */
  getCurrentFontSize(): number {
    return this.doc.getFontSize()
  }

  /**
   * Measure text width
   */
  getTextWidth(text: string): number {
    return this.doc.getTextWidth(text)
  }

  /**
   * Reset to default styling
   */
  resetStyle(): void {
    this.setFont('helvetica', 'normal')
    this.setFontSize(12)
    this.setTextColor(this.config.colors.black)
    this.setDrawColor(this.config.colors.black)
    this.setLineWidth(0.05)
  }

  /**
   * Get PDF as blob
   */
  getBlob(): Blob {
    return this.doc.output('blob')
  }

  /**
   * Get PDF as data URL
   */
  getDataUrl(): string {
    return this.doc.output('dataurlstring')
  }

  /**
   * Save PDF to file
   */
  save(filename: string): void {
    this.doc.save(filename)
  }

  /**
   * Get PDF as array buffer
   */
  getArrayBuffer(): ArrayBuffer {
    return this.doc.output('arraybuffer')
  }
}
